import { PagedResult, panelDelete, panelGet, panelPost, panelPostForm, panelPut, panelPutForm, toQuery } from "./panel.api";
import { SorItem, WorkOrderFormValues } from "../Modules/WorkOrder/Constants";

/**
 * work-order — /work-order (Panel Contract API V4)
 *
 * PATHS
 * V3 exposed create/draft/edit/list under two names each; V4 keeps only the
 * canonical ones used below and drops the `-work-order` / `get-list` aliases.
 * They stay collected in PATHS so a future rename is one edit, not a hunt.
 *
 * TRANSPORT
 * `body` and `templateId` are documented as query params with no request body,
 * which is how springdoc renders Spring's @RequestParam/@RequestPart on a
 * multipart handler — a work order plus its PDFs cannot fit in a URL. They are
 * sent as multipart form fields named exactly `body` and `templateId`, matching
 * what the SPA create flow already does (AddNewSPA.tsx). Spring binds multipart
 * fields to either annotation, so this satisfies both signatures.
 */

/* Create is V4's multipart POST /work-order/create and a draft POST
   /work-order/draft-create (v2.1's JSON routes answer 500); the detail is GET
   /work-order/detail?envelopeId=. Edit, list and the approver queue are not in the
   guide and keep their V4 routes. */
const PATHS = {
  /* Submit and Save as draft are V4's multipart routes; v2.1's JSON
     /work-order and /work-order/draft answer 500 on nprod. */
  create: "/work-order/create",
  draft: "/work-order/draft-create",
  edit: "/work-order/edit",
  list: "/work-order/list",
  approveList: "/work-order/approve/list",
  detail: "/work-order/detail",
  remove: "/work-order/delete",
  logs: "/auditlog/envelope/get-all-logs",
  upload: "/document/panel/upload",
  currentDocuments: "/document/get/documents/current-version",
  mailAttachment: "/work-order/mail-attachment",
  documentVersions: "/document/get/document/versions",
  documentVersion: "/document/get/document/version",
  documentLock: "/workflow/document-lock",
  updateDocument: "/document/update/PanelDocument",
  deleteDocument: "/document/delete/PanelDocument",
  documentVersionMail: "/document/trigger/document-version-mail",
  draftTransfer: "/work-order/draft-transfer",
};

/** Filters accepted by /work-order/list. */
export interface WorkOrderListParams {
  page?: number;
  size?: number;
  searchText?: string;
  order?: string;
  orderBy?: string;
  subsidiary?: string;
  division?: string;
  category?: string;
  /** Comma-separated approach codes (NORMAL_ROTATION, EMERGENCY). */
  procurementApproach?: string;
  startAmount?: number;
  endAmount?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  awardAndStrategyPaper?: string;
  budget?: string;
  /** Scopes the list to one owner — used by the approver view. */
  ownerEmail?: string;
}

export interface WorkOrderRecord {
  envelopeId?: string;
  workOrderNo?: string;
  jobTitle?: string;
  subsidiary?: string;
  division?: string;
  category?: string;
  region?: string;
  vendorName?: string;
  status?: string;
  commencementDate?: string;
  completionDate?: string;
  requesterName?: string;
  requesterEmail?: string;
  createdOn?: string;
  [key: string]: any;
}

/** The subset of WorkOrderRequestDto the wizard fills in. */
export interface WorkOrderCreatePayload {
  templateId: string;
  templateName?: string;
  jobTitle: string;
  projectTitle: string;
  subsidiary: string;
  /** The API's `division` is this UI's Cost Centre. */
  division: string;
  department: string;
  prNo: string;
  background: string;
  scopeOfWork: string;
  budgetStatus: string;
  commencementDate: string;
  completionDate: string;
  category: string;
  region: string;
  subRegion: string;
  procurementApproach: string;
  vendorId: string;
  vendorName: string;
  sorDetails: {
    sorId: string;
    itemName: string;
    description: string;
    uom: string;
    quantity: number;
    rate: number;
    amount: number;
  }[];
  approvalAuthority?: string;
  /**
   * The approval chain, in ONE neutral shape, renamed on the way out: create
   * spells it name / routingOrder, draft-create userName / approvingOrder.
   */
  routing?: RoutingEntry[];
  documentLinkDetails?: { linkTitle: string; link: string }[];
}

export interface RoutingEntry {
  name: string;
  email: string;
  designation: string;
  order: number;
  role: string;
  authorityGroup?: string;
}

/* A reviewer has no designation; the backend takes its role there instead
   ("REVIEWER"). An approver keeps its own (R1, GCFO, …). */
const designationOf = (entry: RoutingEntry) => entry.designation || entry.role;

/** `/work-order/create`'s spelling — draft-create's below is a 400 here. */
const asCreateReviewers = (routing: RoutingEntry[] = []) =>
  routing.map((entry) => ({
    name: entry.name,
    email: entry.email,
    designation: designationOf(entry),
    routingOrder: entry.order,
    role: entry.role,
  }));

/** `/work-order/draft-create`'s own spelling — another one is a 400. */
const asDraftReviewers = (routing: RoutingEntry[] = []) =>
  routing.map((entry) => ({
    userName: entry.name,
    userEmail: entry.email,
    designation: designationOf(entry),
    approvingOrder: entry.order,
    role: entry.role,
  }));

/**
 * The currency the amounts are quoted in; MYR throughout.
 *
 * Create takes the exchange rate, edit's payload does not — matching the two
 * cURLs the backend supplied rather than guessing one shape for both, since an
 * unknown property fails the whole request.
 */
const CREATE_CURRENCY = { currencyCode: "MYR", currencySymbol: "RM", exchangeRate: 1.0 };
const EDIT_CURRENCY = { currencyCode: "MYR", currencySymbol: "RM" };

/**
 * Build the JSON `body` part from what the wizard collected.
 *
 * Shaped to WorkOrderRequestDto. A rejected save listed the properties it
 * accepts, and several of ours were not among them — `requesterName`,
 * `requesterEmail` and `dateRequest` are not part of it at all, while the rest
 * are spelled differently: Cost Centre is `division`, the contract category is
 * `category`, the SOR lines are `sorDetails`, and the vendor splits into
 * `vendorId` and `vendorName`. Sending an unknown property fails the whole
 * request, which is why Save as draft returned 400.
 *
 * Region, Sub Region, Vendor and Category are picked by id, since the cascades
 * need a real FK, and each carries the label alongside it. The DTO wants the
 * label under the plain key and keeps an id only for the vendor.
 */
export const buildWorkOrderBody = (
  values: WorkOrderFormValues,
  items: SorItem[]
): WorkOrderCreatePayload => {
  const sorDetails = items.map((item) => ({
    sorId: item.sorId,
    itemName: item.itemName,
    description: item.description,
    uom: item.uom ?? "",
    quantity: Number(item.quantity) || 0,
    rate: Number(item.rate) || 0,
    amount: Number(item.amount) || 0,
  }));

  return {
    // templateId also travels as its own multipart part; the DTO knows it too.
    templateId: values.templateId,
    jobTitle: values.jobTitle,
    projectTitle: values.jobTitle,
    subsidiary: values.subsidiary,
    division: values.costCentre,
    department: values.department,
    prNo: values.prNo,
    background: values.background,
    scopeOfWork: values.scopeOfWork,
    budgetStatus: values.budgetStatus,
    commencementDate: values.commencementDate,
    completionDate: values.completionDate,
    category: values.contractCategoryName || values.contractCategory,
    region: values.regionName || values.region,
    subRegion: values.subRegionName || values.subRegion,
    procurementApproach: values.procurementApproach,
    vendorId: values.vendor,
    vendorName: values.vendorName || values.vendor,
    sorDetails,
  };
};

/**
 * The multipart request the work-order endpoints take.
 *
 * `body` is the only field they read — `templateId` lives INSIDE it, and
 * neither create nor draft-create accepts a binary upload; documents travel as
 * `documentLinks` / `documentLinkDetails` in the JSON. Both a second
 * `templateId` part and a `files` part used to be appended, and the handlers
 * declare neither, so the whole request came back 400.
 */
const toForm = (payload: any) => {
  const form = new FormData();
  form.append("body", JSON.stringify(payload));
  return form;
};

/**
 * The parts create / draft-create take beside `body` (Postman: `body` and
 * `templateId`; e-POD's create sends both too), plus the record a call is about.
 */
const withEnvelope = (form: FormData, envelopeId?: string, templateId?: string) => {
  if (templateId) form.append("templateId", templateId);
  if (envelopeId) form.append("envelopeId", envelopeId);
  return form;
};

/** The id a document READ takes: a draft's documents are read under its
    "-draft" id, a submitted work order's under the plain one. Uploads always
    send the plain id (uploadDocument). */
const documentReadId = (envelopeId: string, draft: boolean) =>
  `${envelopeId.replace(/-draft$/i, "")}${draft ? "-draft" : ""}`;

export const workOrderApi = {
  /** Paged, filterable list backing the Work Order and Approval screens. */
  list: (params: WorkOrderListParams = {}) =>
    panelGet<PagedResult<WorkOrderRecord>>(PATHS.list, {
      page: 0,
      size: 20,
      orderBy: "createdOn",
      ...params,
    }),

  /**
   * The approver's queue — the Work Order Approval screen.
   *
   * GET /work-order/approve/list?page&size&isCompleted&searchText&order&orderBy
   *   &subsidiary&division&category&startAmount&endAmount&startDate&endDate
   *   &status&ownerEmail
   *
   * `isCompleted` splits pending from completed, `ownerEmail` scopes it to one
   * approver, and `status` is a code (PENDING), not a label.
   */
  approveList: (params: WorkOrderListParams & { isCompleted?: boolean } = {}) =>
    panelGet<PagedResult<WorkOrderRecord>>(PATHS.approveList, {
      page: 0,
      size: 20,
      orderBy: "createdOn",
      ...params,
    }),

  /** Records are addressed by envelopeId, as a query param: GET /work-order/detail?envelopeId=. */
  detail: (envelopeId: string) => panelGet<WorkOrderRecord>(PATHS.detail, { envelopeId }),

  remove: (envelopeId: string) => panelDelete(PATHS.remove, { envelopeId }),

  /**
   * One document into a work order's store: the envelope, the document's id,
   * whether the work order is still a draft, and the file itself as
   * `create-document-file`. No docVersion — the backend assigns the version.
   */
  uploadDocument: ({
    envelopeId,
    documentId,
    draft,
    file,
  }: {
    envelopeId: string;
    documentId: number | string;
    draft: boolean;
    file: File;
  }) => {
    const form = new FormData();
    /* A draft's id carries a "-draft" suffix; its documents are stored under the
       envelope id without it. */
    form.append("envelopeId", envelopeId.replace(/-draft$/i, ""));
    form.append("documentId", String(documentId));
    form.append("draft", String(draft));
    form.append("create-document-file", file);
    return panelPostForm(PATHS.upload, form);
  },

  /**
   * Several files, one request each and one after another, ids counting up
   * from `firstDocumentId` — e-POD's handleUpload. Sequential so two uploads
   * never race for the same id.
   */
  uploadDocuments: async ({
    files,
    firstDocumentId,
    ...rest
  }: {
    envelopeId: string;
    files: File[];
    firstDocumentId: number;
    draft: boolean;
  }) => {
    for (let index = 0; index < files.length; index += 1) {
      await workOrderApi.uploadDocument({ ...rest, documentId: firstDocumentId + index, file: files[index] });
    }
  },

  /**
   * Delete documents from a panel contract work order — e-POD's /delete/PanelDocument.
   * deleteFormData: envelopeId, delete_id, existing_id, existing_file_names, versionOrder, versionKey
   */
  deletePanelDocument: (form: FormData) => panelPostForm(PATHS.deleteDocument, form),

  /**
   * Update / add document to a panel contract work order — e-POD's /update/PanelDocument.
   * createFormData: envelopeId, create-document-file, create_id, versionOrder, versionKey
   */
  updatePanelDocument: (form: FormData) => panelPutForm(PATHS.updateDocument, form),

  /**
   * Lock documents for a new version — e-POD's / SPA's PUT /workflow/document-lock.
   */
  documentLock: ({
    envelopeId,
    versionOrder,
    versionKey,
    draft = false,
  }: {
    envelopeId: string;
    versionOrder: number | string;
    versionKey: string;
    draft?: boolean;
  }) => {
    const cleanId = envelopeId.replace(/-draft$/i, "");
    const params: Record<string, any> = {
      envelopeId: cleanId,
      versionOrder,
      versionKey,
    };
    if (draft) params.draft = "true";
    return panelPut(PATHS.documentLock, {}, params);
  },

  /**
   * Trigger document version mail — e-POD's / SPA's PUT /document/trigger/document-version-mail.
   */
  triggerDocumentVersionMail: ({
    envelopeId,
    versionOrder,
    draft = false,
  }: {
    envelopeId: string;
    versionOrder: number | string;
    draft?: boolean;
  }) => {
    const cleanId = envelopeId.replace(/-draft$/i, "");
    const params: Record<string, any> = {
      envelopeId: draft ? `${cleanId}-draft` : cleanId,
      versionOrder,
    };
    if (draft) params.draft = "true";
    return panelPut(PATHS.documentVersionMail, {}, params);
  },

  /**
   * Transfer draft documents to the final envelope directory in Azure Blob Storage.
   * Endpoint: PUT https://spa-nprod-panel.myairports.com.my/work-order/draft-transfer?envelopeId=<envelopeId>
   */
  draftTransfer: (envelopeId: string) => {
    if (!envelopeId) return Promise.resolve();
    const cleanId = envelopeId.replace(/-draft$/i, "");
    return panelPut(PATHS.draftTransfer, null, { envelopeId: cleanId });
  },

  /**
   * The work order's audit trail, paged — rows carry `action`, `detail`,
   * `actionPerformedBy` and `actionPerformedOn`, as the SPA's own logs do.
   * Every parameter is sent, `searchText` even when empty (toQuery would drop
   * it), the way the endpoint is called.
   */
  logs: ({
    envelopeId,
    page = 0,
    size = 10,
    searchText = "",
    order = "desc",
    orderBy = "actionPerformedOn",
  }: { envelopeId: string; page?: number; size?: number; searchText?: string; order?: string; orderBy?: string }) =>
    panelGet<PagedResult<any>>(
      `${PATHS.logs}?${new URLSearchParams({
        envelopeId,
        page: String(page),
        size: String(size),
        searchText,
        order,
        orderBy,
      })}`
    ),

  /**
   * The work order's documents as they stand now — e-POD's current-version:
   * an array of { documentId, documentName, blobUrl, docVersion, ... }.
   * Always reads using clean envelopeId (without -draft).
   */
  currentDocuments: (envelopeId: string, draft = false) => {
    if (draft || /-draft$/i.test(envelopeId)) {
      return Promise.resolve([]);
    }
    return panelGet<any[]>(PATHS.currentDocuments, { envelopeId: envelopeId.replace(/-draft$/i, "") });
  },

  /**
   * Actions -> Send: mail the work order PDF — e-POD's /spa/mail-attachment body,
   * field for field (recipients as arrays, the PDF as base64 in `baseString`).
   */
  mailAttachment: (payload: {
    mailSubject: string;
    mailMessage: string;
    mails: string[];
    mailsCC: string[];
    mailsBCC: string[];
    senderEmail?: string;
    baseString: string;
    pdfKey: string;
    pdfName: string;
  }) => panelPost(PATHS.mailAttachment, payload),

  /** One page of the work order's document versions, sent the way logs are. */
  /** One version's files — GET /document/get/document/version. */
  versionDocuments: (envelopeId: string, docVersion: string | number = 1, draft = false) => {
    if (draft || /-draft$/i.test(envelopeId)) {
      return Promise.resolve({ data: [] });
    }
    const cleanId = envelopeId.replace(/-draft$/i, "");
    let versionPayload = 1;
    if (typeof docVersion === "number") {
      versionPayload = docVersion;
    } else {
      const s = String(docVersion).trim();
      if (s.includes(".")) {
        const parts = s.split(".");
        const minor = parseInt(parts[1], 10);
        versionPayload = !isNaN(minor) ? (minor === 0 ? 1 : minor) : 1;
      } else {
        const parsed = parseInt(s, 10);
        versionPayload = !isNaN(parsed) ? parsed : 1;
      }
    }
    if (versionPayload === 0) {
      return Promise.resolve({ data: [] });
    }
    return panelGet<any>(PATHS.documentVersion, {
      envelopeId: cleanId,
      docVersion: versionPayload,
    });
  },

  documentVersions: ({
    envelopeId,
    draft = false,
    page = 0,
    size = 10,
    searchText = "",
    order = "desc",
    orderBy = "docVersion",
  }: {
    envelopeId: string;
    draft?: boolean;
    page?: number;
    size?: number;
    searchText?: string;
    order?: string;
    orderBy?: string;
  }) => {
    if (draft || /-draft$/i.test(envelopeId)) {
      return Promise.resolve({ data: [], records: [], totalCount: 0 });
    }
    return panelGet<PagedResult<any>>(
      `${PATHS.documentVersions}?${new URLSearchParams({
        envelopeId: envelopeId.replace(/-draft$/i, ""),
        page: String(page),
        size: String(size),
        order,
        searchText,
        orderBy,
      })}`
    );
  },

  /**
   * Submit — V4's create: multipart `body`, its own reviewer spelling. With an
   * `envelopeId` it publishes that draft as an active work order (the backend's
   * route for submitting a draft); it travels in the body and as its own part.
   */
  create: ({ routing, ...payload }: any) =>
    panelPostForm(
      PATHS.create,
      withEnvelope(
        toForm({ ...payload, currency: CREATE_CURRENCY, reviewers: asCreateReviewers(routing) }),
        payload.envelopeId,
        payload.templateId
      )
    ),

  /** Save as draft — V4's draft-create: multipart `body`, its own reviewer
      spelling. With an `envelopeId` it updates that draft instead of adding one. */
  createDraft: ({ routing, ...payload }: any) =>
    panelPostForm(
      PATHS.draft,
      withEnvelope(toForm({ ...payload, reviewers: asDraftReviewers(routing) }), payload.envelopeId, payload.templateId)
    ),

  /**
   * Edit. `envelopeId` goes in the query string. A submitted work order's
   * routing is not part of this body — it is updated separately through
   * /workflow/edit-reviewer. A draft's is: its reviewers live on the work
   * order, so they go in draft-create's spelling. Left out when empty, so a
   * save from before the Reviewers step does not wipe them.
   */
  edit: ({ routing, envelopeId, ...payload }: any, draft = false) =>
    panelPutForm(
      PATHS.edit + toQuery({ envelopeId }),
      toForm({
        ...payload,
        envelopeId,
        currency: EDIT_CURRENCY,
        ...(draft && routing?.length && { reviewers: asDraftReviewers(routing) }),
      })
    ),
};
